/**
 * GTIN: normalización y dígito verificador. **Espejo exacto de
 * `services/api/schemas/gs1.py`.**
 *
 * Por qué se valida también en el cliente, si la API ya lo hace
 * -----------------------------------------------------------
 * La API es la autoridad y sigue siéndolo: este archivo no autoriza nada y su
 * resultado no se manda como "ya validado". Lo que aporta es el criterio de
 * aceptación de #38 — *validación de GTIN en el formulario, con mensaje
 * claro*. Un 422 devuelve "el dígito verificador del GTIN no es válido", pero
 * llega después del viaje y no dice CUÁL debería ser. Quien carga un catálogo
 * de doscientos SKU copiando códigos de cajas necesita saber, mientras escribe,
 * que el último dígito tendría que ser un 5.
 *
 * Que sea un espejo importa: si diverge, el formulario acepta algo que la API
 * rechaza (molesto) o rechaza algo que la API acepta (peor: un producto que no
 * se puede dar de alta desde el dashboard). `tests/products.test.ts` corre los
 * mismos vectores que `tests/test_gs1.py`.
 *
 * Reglas de GS1, tal cual están en el módulo de Python:
 * · se aceptan GTIN-8 / 12 / 13 / 14 y se normalizan a 14 con ceros a la
 *   izquierda — el relleno preserva el dígito verificador, así que un EAN-13
 *   de la caja se pega tal cual;
 * · vacío es válido y significa "sin GTIN": el cliente de cosmética que aún no
 *   serializa bajo GS1 no puede quedar afuera del catálogo.
 */

/** Longitud del campo GS1 `(01)`. */
export const GTIN_LENGTH = 14;

/** Longitudes admitidas en la entrada: EAN-8, UPC-A, EAN-13 y GTIN-14. */
export const VALID_INPUT_LENGTHS: readonly number[] = [8, 12, 13, 14];

/** Separadores que un humano copia y pega desde una caja o una planilla. */
const STRIP = new Set([' ', '-', '\t', ' ', '.']);

export type GtinProblem = 'notDigits' | 'length' | 'checkDigit';

export type GtinResult =
  | { ok: true; value: string | null }
  | { ok: false; problem: 'notDigits' }
  | { ok: false; problem: 'length'; length: number }
  | { ok: false; problem: 'checkDigit'; expected: number };

/**
 * Dígito verificador GS1 de los primeros 13 dígitos de un GTIN-14.
 * Ponderación 3/1 desde la derecha, complemento a 10.
 */
export function gtinCheckDigit(body: string): number {
  let total = 0;
  for (let position = 0; position < body.length; position += 1) {
    const digit = Number(body[body.length - 1 - position]);
    total += digit * (position % 2 === 0 ? 3 : 1);
  }
  return (10 - (total % 10)) % 10;
}

/** Quita separadores. No valida nada. */
export function cleanGtin(raw: string): string {
  return Array.from(raw)
    .filter((char) => !STRIP.has(char))
    .join('');
}

/**
 * Normaliza a 14 dígitos y valida el verificador.
 *
 *     normalizeGtin('7891234567895')  → { ok: true, value: '07891234567895' }
 *     normalizeGtin('7891234567890')  → { ok: false, problem: 'checkDigit', expected: 5 }
 *     normalizeGtin('')               → { ok: true, value: null }
 */
export function normalizeGtin(raw: string | null | undefined): GtinResult {
  if (raw === null || raw === undefined) return { ok: true, value: null };
  const cleaned = cleanGtin(raw);
  if (cleaned.length === 0) return { ok: true, value: null };
  if (!/^\d+$/.test(cleaned)) return { ok: false, problem: 'notDigits' };
  if (!VALID_INPUT_LENGTHS.includes(cleaned.length)) {
    return { ok: false, problem: 'length', length: cleaned.length };
  }
  const padded = cleaned.padStart(GTIN_LENGTH, '0');
  const expected = gtinCheckDigit(padded.slice(0, -1));
  if (Number(padded.slice(-1)) !== expected) {
    return { ok: false, problem: 'checkDigit', expected };
  }
  return { ok: true, value: padded };
}

/**
 * Cadena de elemento GS1 del producto para el DataMatrix, sin serial.
 * Sólo `(01)`: `(21)` lleva el **código impreso** y nunca el `unit_id`, y
 * `(10)`/`(17)` los aporta el lote, que se asigna en la activación.
 */
export function gs1ProductElement(gtin: string | null | undefined): string | null {
  return gtin === null || gtin === undefined || gtin === '' ? null : `(01)${gtin}`;
}
